import { Layout, Skeleton, theme } from "antd";
import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./layout/sidebar";
import { Wrapper } from "./style";
import { TableLoading } from "../components/table-animation";
import { BorderFilter } from "../config";
import { Admins } from "../routers/admin";
import { Users } from "../routers/user";

const { Content } = Layout;

const Page = (props) => {
  const { user, setData } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const [reloadPage, setReloadPage] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);

  const handleLogout = () => {
    setData({});
    navigate("/login");
  };

  useEffect(() => {
    if (Object.keys(user)?.length === 0) {
      handleLogout();
    }

    setLoading(false);

    const handleStorage = (e) => {
      if (e?.storageArea?.length === 0) {
        setData({});
        return navigate("/login");
      }

      if (user) {
        const menusName = (
            !Object.keys(user)?.includes("admin") ? Admins : Users
        )?.map((d) => `/${d?.path}`);

        navigate(
            menusName?.includes(`${location.pathname}`)
                ? window.location.pathname
                : `${menusName?.[0]}`
        );
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [user]);

  // --- Logic for Breakpoints & Attributes ---
  useEffect(() => {
    const breakpoint = 992;
    document.body.setAttribute("data-kt-app-header-enabled", "false");
    document.body.setAttribute("data-kt-app-header-fixed", "false");

    const syncAttributes = () => {
      if (window.innerWidth < breakpoint) {
        document.body.setAttribute("data-kt-app-sidebar-minimize", "off");
        document.body.setAttribute(
            "data-kt-drawer",
            isSidebarMinimized ? "on" : "off"
        );
      } else {
        document.body.setAttribute(
            "data-kt-app-sidebar-minimize",
            isSidebarMinimized ? "on" : "off"
        );
        document.body.setAttribute("data-kt-drawer", "off");
      }
    };

    syncAttributes();
    window.addEventListener("resize", syncAttributes);
    return () => window.removeEventListener("resize", syncAttributes);
  }, [isSidebarMinimized]);

  useEffect(() => {
    const sidebar = document.getElementById("kt_app_sidebar");
    const breakpoint = 992;

    const applySidebarClasses = () => {
      if (window.innerWidth < breakpoint) {
        sidebar?.classList.add("drawer", "drawer-start");
      } else {
        sidebar?.classList.remove("drawer", "drawer-start", "drawer-on");
        const overlay = document.getElementById("drawer-overlay");
        overlay?.remove();
      }
    };

    applySidebarClasses();
    window.addEventListener("resize", applySidebarClasses);
    return () => window.removeEventListener("resize", applySidebarClasses);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarMinimized((prev) => {
      const newState = !prev;
      const sidebar = document.getElementById("kt_app_sidebar");
      const overlayId = "drawer-overlay";
      const breakpoint = 992;

      if (window.innerWidth < breakpoint) {
        // Drawer logic for mobile < 992px
        let overlay = document.getElementById(overlayId);
        if (newState) {
          sidebar?.classList.add("drawer-on");
          if (!overlay) {
            overlay = document.createElement("div");
            overlay.id = overlayId;
            overlay.className = "drawer-overlay";
            overlay.style.cssText =
                "z-index:105; position:fixed; top:0; left:0; width:100vw; height:100vh; background:rgba(0,0,0,0.5); transition: opacity 0.3s ease;";
            document.body.appendChild(overlay);
            overlay.addEventListener("click", () => {
              setIsSidebarMinimized(false);
              sidebar?.classList.remove("drawer-on");
              overlay.remove();
            });
          }
        } else {
          sidebar?.classList.remove("drawer-on");
          overlay?.remove();
        }
      } else {
        // Minimize logic for desktop >= 992px
        if (newState) {
          sidebar?.classList.add("sidebar-minimized");
        } else {
          sidebar?.classList.remove("sidebar-minimized");
        }
      }
      return newState;
    });
  };

  return (
      <div
          className="app-page flex-column flex-column-fluid"
          id="kt_app_page"
          style={{ backgroundColor: "#F1F2F4" }}
      >
        <style>{`
          @media (max-width: 991px) {
            .mobile-fab {
              display: flex !important;
              position: fixed;
              bottom: 24px;
              right: 24px;
              z-index: 200;
              width: 44px;
              height: 44px;
              border-radius: 50%;
              align-items: center;
              justify-content: center;
              box-shadow: 0 4px 16px rgba(0,0,0,0.18);
            }
          }
          @media (min-width: 992px) {
            .mobile-fab {
              display: none !important;
            }
          }
        `}</style>

        <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">

          <Sidebar
              id="kt_app_sidebar"
              user={user}
              setUser={setData}
              setData={setData}
              type={user?.loginType}
              collapsed={isSidebarMinimized}
              location={location}
              handleLogout={handleLogout}
              toggleSidebar={toggleSidebar}
          />

          <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
            {/* Subscription banner - non-dismissible. Shows days left based on user.user.user_data.trial_expires_at */}
            {(() => {
              const trialExpires = user?.user?.user_data?.trial_expires_at;
              if (!trialExpires) return null;
              const expiresAtMs = parseInt(trialExpires, 10) * 1000;
              const now = Date.now();
              const diffDays = Math.ceil((expiresAtMs - now) / (1000 * 60 * 60 * 24));
              let msg = '';
              if (diffDays > 1) msg = `Your subscription expires in ${diffDays} days.`;
              else if (diffDays === 1) msg = `Your subscription expires in 1 day.`;
              else if (diffDays === 0) msg = `Your subscription expires today.`;
              else msg = `Your subscription has expired.`;

              return (
                <div
                  role="status"
                  aria-live="polite"
                  className="subscription-banner d-flex align-items-center justify-content-center"
                  style={{
                    background: '#FFF4E6',
                    color: '#7A4B00',
                    padding: '8px 12px',
                    fontWeight: 600,
                    fontSize: '14px',
                    borderBottom: '1px solid rgba(0,0,0,0.05)'
                  }}
                >
                  {msg}
                </div>
              );
            })()}
            <div className="d-flex flex-column flex-column-fluid">
              <div id="kt_app_content">
                <div id="kt_app_content_container" className="app-container container-fluid">
                  {reloadPage ? (
                      <Wrapper>
                        <Outlet />
                      </Wrapper>
                  ) : (
                      <>
                        <div
                            className="card mb-3 mt-4 px-3"
                            style={{ border: BorderFilter }}
                        >
                          <div className="card-header d-flex justify-content-between align-items-center">
                            <div className="card-title d-flex align-items-center">
                              <Skeleton.Input active />
                            </div>
                            <div className="card-toolbar">
                              <Skeleton.Button active />
                            </div>
                          </div>
                        </div>

                        <div className="card">
                          <div style={{ padding: "0px 15px", marginTop: "10px" }}>
                            <TableLoading
                                id="test-table"
                                columns={[1, 2, 3, 4, 5]}
                                checkBoxVal={true}
                                actions={[1, 2]}
                            />
                          </div>
                        </div>
                      </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom-right FAB — mobile only, outside content flow so it never overlaps page buttons */}
        <button
            onClick={toggleSidebar}
            className="btn btn-primary mobile-fab"
            style={{ display: "none" }}
            title="Toggle Sidebar"
        >
          <i className="ki-outline ki-abstract-14 fs-2"></i>
        </button>

      </div>
  );
};

export default Page;